import type { EmployeePenalty, PenaltyConfig, LateArrivalTierRule, PaginatedResponse } from '@pms/shared';
import type { TierRuleInput } from '@pms/shared';
import { api } from '@/lib/api';
import { downloadXlsx, localFileStamp } from '@/lib/download';

export const penaltyService = {
  async createManual(input: {
    user_id: string;
    attendance_date: string;
    penalty_minutes: number;
    reason: string;
  }): Promise<EmployeePenalty> {
    const res = await api.post<{ data: EmployeePenalty }>('/penalty/manual', input);
    return res.data.data;
  },

  async list(params: {
    status?: 'pending' | 'approved' | 'rejected';
    user_id?: string;
    q?: string;
    page?: number;
    limit?: number;
    date_from?: string;
    date_to?: string;
  }): Promise<PaginatedResponse<EmployeePenalty>> {
    const res = await api.get<PaginatedResponse<EmployeePenalty>>('/penalty', { params });
    return res.data;
  },

  /**
   * Downloads every penalty matching the page's tab and filters (all pages, not just
   * the loaded one) as .xlsx: a details sheet plus a per-employee summary. The
   * server reads the data at request time, so every click is current.
   */
  async exportXlsx(params: {
    status?: 'pending' | 'approved' | 'rejected';
    q?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<void> {
    await downloadXlsx(
      api.get('/penalty/export/xlsx', { params, responseType: 'blob' }),
      `penalties-${params.status ?? 'all'}-${localFileStamp()}.xlsx`,
    );
  },

  async approve(id: string): Promise<EmployeePenalty> {
    const res = await api.patch<{ data: EmployeePenalty }>(`/penalty/${id}/approve`);
    return res.data.data;
  },

  async reject(id: string, rejection_reason: string): Promise<EmployeePenalty> {
    const res = await api.patch<{ data: EmployeePenalty }>(`/penalty/${id}/reject`, {
      rejection_reason,
    });
    return res.data.data;
  },

  async moveToPending(id: string): Promise<EmployeePenalty> {
    const res = await api.patch<{ data: EmployeePenalty }>(`/penalty/${id}/move-to-pending`);
    return res.data.data;
  },

  async generate(params?: {
    date?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<{ generated: number; date_from: string; date_to: string }> {
    const res = await api.post<{ data: { generated: number; date_from: string; date_to: string } }>(
      '/penalty/generate',
      params ?? {},
    );
    return res.data.data;
  },

  async getConfig(): Promise<PenaltyConfig> {
    const res = await api.get<{ data: PenaltyConfig }>('/penalty/config');
    return res.data.data;
  },

  async updateConfig(input: {
    miss_punch_minutes: number;
    late_arrival_min_threshold: number;
    exclude_sundays_late_arrival: boolean;
    exclude_sundays_miss_punch: boolean;
    auto_generate_enabled: boolean;
    penalty_gap_days: number;
    late_leave_correction_enabled: boolean;
    penalty_rate_mode: 'per_hour' | 'global_rate' | 'multiplier' | 'fixed_amount';
    penalty_rate_per_hour: number | null;
    penalty_rate_multiplier: number;
    penalty_fixed_amount: number | null;
  }): Promise<PenaltyConfig> {
    const res = await api.patch<{ data: PenaltyConfig }>('/penalty/config', input);
    return res.data.data;
  },

  async getTierRules(): Promise<LateArrivalTierRule[]> {
    const res = await api.get<{ data: LateArrivalTierRule[] }>('/penalty/tier-rules');
    return res.data.data;
  },

  async upsertTierRules(rules: TierRuleInput[]): Promise<LateArrivalTierRule[]> {
    const res = await api.put<{ data: LateArrivalTierRule[] }>('/penalty/tier-rules', { rules });
    return res.data.data;
  },

  async modifyPenalty(
    id: string,
    input: { penalty_minutes: number; reason?: string },
  ): Promise<EmployeePenalty> {
    const res = await api.patch<{ data: EmployeePenalty }>(`/penalty/${id}/modify`, input);
    return res.data.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/penalty/${id}`);
  },

  async listMy(params: {
    page?: number;
    limit?: number;
    status?: 'pending' | 'approved' | 'rejected';
    date_from?: string;
    date_to?: string;
  }): Promise<PaginatedResponse<EmployeePenalty>> {
    const res = await api.get<PaginatedResponse<EmployeePenalty>>('/penalty/my', { params });
    return res.data;
  },
};
